import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
	SectionHeading,
	SectionHeadingTitle,
	SectionHeadingIcons,
} from '@jira/issue__section-header/src/section-heading-view.tsx';
import type { ScrollEvent } from '@atlaskit/media-filmstrip';
import { useIsReducedMotion } from '@atlaskit/motion';
import { Stack } from '@atlaskit/primitives/compiled';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import type {
	WebLink,
	SmartWebLink,
} from '@atlassian/jira-issue-view-common-types/src/web-links-type.tsx';
import { useLoomRecorderTriggerListener } from '@atlassian/jira-loom-utils/src/controllers/use-loom-recorder-trigger/index.tsx';
import { LoomRecorderTriggerEvent } from '@atlassian/jira-loom-utils/src/controllers/use-loom-recorder-trigger/types.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { messages } from '../../messages.tsx';
import LoomActionsMenu from './loom-actions-menu/index.tsx';
import { LoomVideoInsert } from './loom-video-insert/index.tsx';
import { LoomVideosList } from './loom-videos-list/index.tsx';
import type { Props } from './types.tsx';

function isSmartAndNotFailedToDeleteWebLink(link: WebLink): link is SmartWebLink {
	return 'smartLinkId' in link && !('deleteFailed' in link);
}

export const LoomCoUseView = ({
	isInsertLoomLinkOpened,
	insertLoomLinkClickedCount,
	links,
	onInsertLink,
	onDeleteLink,
	onShowInsertLoomLinkForm,
	onHideInsertLoomLinkForm,
}: Props) => {
	const { formatMessage } = useIntl();
	const isReducedMotion = useIsReducedMotion();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const optimisticlyCreateLinkIdRef = useRef<string | undefined>();

	const validLinks = useMemo(
		() => links.filter((link) => !('isCreateFailed' in link && link.isCreateFailed)),
		[links],
	);

	const previousLinks = usePrevious(links.filter(isSmartAndNotFailedToDeleteWebLink));
	// This effect shows flags when a new Loom link is inserted (via recording or via adding an existing link)
	useEffect(() => {
		// Filtering out results of failed deletions (the link is deleted optimistically and if that fails it appears again)
		const smartLinks: SmartWebLink[] = links.filter(isSmartAndNotFailedToDeleteWebLink);
		if (!optimisticlyCreateLinkIdRef.current) {
			// if we're here, we search for a newly created optimistic link
			const optimisticItem = smartLinks
				.filter((link) => Object.hasOwnProperty.call(link, 'isCreateFailed'))
				// there might be other links that the user tried and failed to create before but we only care about the latest one.
				.pop();
			if (
				optimisticItem &&
				// It's the first time we ever see this link (optimistic)
				!previousLinks?.find((link) => link.smartLinkId === optimisticItem.smartLinkId)
			) {
				optimisticlyCreateLinkIdRef.current = optimisticItem.smartLinkId;
			}
			return;
		}

		// if we're here, an optimistic link we in the previous run was sent to the server and came back with a result we want to check
		const createdItem = smartLinks.find(
			(link) => link.smartLinkId === optimisticlyCreateLinkIdRef.current,
		);
		optimisticlyCreateLinkIdRef.current = undefined;
		if (!createdItem) {
			// if we got here - it is a bug, should never happen
			fireErrorAnalytics({
				meta: {
					id: 'showFlagLoomOptimisticLinkLookup',
					packageName: 'jiraIssueViewBase',
					teamName: 'bento',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			return;
		}
		if ('isCreateFailed' in createdItem && createdItem.isCreateFailed) {
			showFlag({
				id: 'loomInsertLinkFailure',
				type: 'error',
				title: messages.failureTitleLinkInserted,
				description: formatMessage(messages.failureLinkInserted),
				messageId:
					'issue-view-base.content.loom-videos.ui.co-use.show-flag.error.loom-insert-link-failure',
				messageType: 'transactional',
			});
			fireErrorAnalytics({
				meta: {
					id: 'loomInsertLink',
					packageName: 'jiraIssueViewBase',
					teamName: 'bento',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		} else {
			showFlag({
				id: 'loomInsertLinkSuccess',
				type: 'success',
				title: messages.successTitleLinkInserted,
				description: formatMessage(messages.successLinkInserted),
				messageId:
					'issue-view-base.content.loom-videos.ui.co-use.show-flag.success.loom-insert-link-success',
				messageType: 'transactional',
				actions: [
					{
						content: messages.successActionShare,
						onClick: () => {
							if (typeof navigator !== 'undefined') {
								navigator.clipboard.writeText(createdItem.href);
							}
						},
					},
				],
			});
		}
	}, [links, showFlag, formatMessage, previousLinks]);

	// when cross flow succeeds, open the recorder
	useLoomRecorderTriggerListener(LoomRecorderTriggerEvent.CROSS_FLOW_SUCCESS, {
		entryPointLocation: 'jiraIssueView_quickAdd_crossflow', // TODO confirm what we want for these strings
		// intentionally not localized because Loom RecordSDK does not support i18n
		insertButtonText: 'Add video',
		onInsert: onInsertLink,
	});

	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});

	// animate and offset are used to control the LoomVideosList
	const [animate, setAnimate] = useState<boolean>(false);
	const [offset, setOffset] = useState<number>(0);

	// handleScroll is passed to the LoomVideosList to update the animate and offset states
	const handleScroll = useCallback(
		(e: ScrollEvent) => {
			if (e.direction === 'left') {
				fireUIAnalytics(analyticsEvent, 'loom-video-list-left-icon-button');
			} else {
				fireUIAnalytics(analyticsEvent, 'loom-video-list-right-icon-button');
			}
			setOffset(e.offset);
			if (isReducedMotion) {
				setAnimate(false);
			} else {
				setAnimate(e.animate);
			}
		},
		[analyticsEvent, isReducedMotion],
	);

	const handleLinkInsert = async (url: string) => {
		onInsertLink(url);
		fireUIAnalytics(analyticsEvent, 'insert-existing-loom-link-button');
		// reset the LoomVideosList to show the newly inserted link
		setOffset(0);
		if (isReducedMotion) {
			setAnimate(false);
		} else {
			setAnimate(true);
		}
	};

	const handleCancelLinkInsert = () => {
		fireUIAnalytics(analyticsEvent, 'cancel-existing-loom-link-button');
		onHideInsertLoomLinkForm();
	};

	// only show the section heading if there are links or the insert link form is opened (this behavior is consistent with the existing web links implementation)
	const shouldShowSection = isInsertLoomLinkOpened || links.length > 0;
	const enableVisualRefreshChanges = isVisualRefreshEnabled();

	if (!shouldShowSection && enableVisualRefreshChanges) {
		return null;
	}

	return (
		<Stack testId="issue-view-base.content.loom-videos.ui.co-use.container">
			{shouldShowSection && (
				<SectionHeading data-testid="issue-view-base.content.loom-videos.ui.co-use.heading">
					<SectionHeadingTitle>{formatMessage(messages.loomVideosHeading)}</SectionHeadingTitle>
					<SectionHeadingIcons>
						<LoomActionsMenu
							onShowInsertLoomLinkForm={onShowInsertLoomLinkForm}
							onInsertLink={onInsertLink}
						/>
					</SectionHeadingIcons>
				</SectionHeading>
			)}
			{validLinks.length > 0 && (
				<LoomVideosList
					links={validLinks}
					animate={animate}
					offset={offset}
					onScroll={handleScroll}
					onDeleteLink={onDeleteLink}
				/>
			)}
			{isInsertLoomLinkOpened && (
				<LoomVideoInsert
					insertLoomLinkClickedCount={insertLoomLinkClickedCount}
					loomLinkCount={validLinks.length}
					onInsert={handleLinkInsert}
					onCancel={handleCancelLinkInsert}
				/>
			)}
		</Stack>
	);
};
